import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  TrendingUp,
  Eye,
  Award,
  MapPin,
  Phone,
  Mail,
  ArrowLeft,
  ChevronRight,
  FileText,
  Shield,
} from 'lucide-react';
import { Wordmark, Eyebrow, CtaChevrons, ConsultationForm } from './shared';

const STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline:
    'Pathology Billing Case Study: How Horizon RCM Resolved Chronic Modifier Errors and Achieved a 92% Net Collection Rate',
  description:
    'An independent pathology laboratory in Texas had cycled through multiple billing companies, each leaving behind compounding modifier errors and zero transparency. Horizon RCM corrected the Modifier 26 compliance issue on day one, built claim-level accessibility into the practice reporting structure, and drove net collections above 92 percent within the first full quarter.',
  author: {
    '@type': 'Organization',
    name: 'Horizon RCM',
    url: 'https://horizon-rcm.com',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Horizon RCM',
    logo: {
      '@type': 'ImageObject',
      url: 'https://horizon-rcm.com/logo512.png',
    },
  },
  datePublished: '2025-01-01',
  dateModified: '2025-07-01',
  mainEntityOfPage:
    'https://horizon-rcm.com/case-studies/pathology-billing-modifier-accuracy',
  keywords:
    'pathology billing case study, modifier 26 pathology billing, independent pathology lab RCM, pathology billing results, pathology revenue cycle management',
  about: [
    { '@type': 'Thing', name: 'Pathology Billing' },
    { '@type': 'Thing', name: 'Revenue Cycle Management' },
    { '@type': 'Thing', name: 'Modifier 26 Compliance' },
    { '@type': 'Thing', name: 'Medical Billing Transparency' },
  ],
};

const stats = [
  {
    icon: TrendingUp,
    value: '> 92%',
    label: 'Net Collection Rate',
    detail: 'Maintained consistently after the first full quarter',
  },
  {
    icon: Eye,
    value: 'On-Demand',
    label: 'Claim Lookup',
    detail: 'Full claim-level transparency available any time',
  },
  {
    icon: Award,
    value: '100%',
    label: 'Modifier Accuracy',
    detail: 'Modifier 26 applied correctly from day one',
  },
];

const outcomes = [
  'Net collection rate exceeded 92% within the first full quarter and has remained above that threshold consistently',
  'Modifier 26 compliance issue resolved entirely on day one — zero recurrence',
  'Denial rates dropped materially in the first 90 days as payer-specific submission issues were corrected',
  'Provider gained on-demand access to every individual claim, payment status, and submission history',
  'Spot-check process became a routine governance practice rather than a reactive audit mechanism',
  'Credentialing renewals moved from reactive spreadsheet tracking to proactive deadline management',
];

function CaseStudyDrJim() {
  useEffect(() => {
    const previousTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    const previousDescription = metaDescription?.getAttribute('content') ?? '';
    const previousKeywords = metaKeywords?.getAttribute('content') ?? '';
    const previousCanonical = canonicalLink?.getAttribute('href') ?? '';

    document.title =
      'Pathology Billing Case Study: 92% Collection Rate & Modifier Accuracy | Horizon RCM';

    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'How Horizon RCM resolved chronic Modifier 26 billing errors for an independent Texas pathology lab — achieving >92% net collection rate, 100% modifier accuracy, and full on-demand claim transparency. Read the full case study.'
      );
    }
    if (metaKeywords) {
      metaKeywords.setAttribute(
        'content',
        'pathology billing case study, modifier 26 pathology billing, pathology billing results, independent pathology lab RCM, pathology revenue cycle management, pathology billing transparency, professional component billing, pathology net collection rate'
      );
    }
    if (canonicalLink) {
      canonicalLink.setAttribute(
        'href',
        'https://horizon-rcm.com/case-studies/pathology-billing-modifier-accuracy'
      );
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'case-study-schema';
    script.textContent = JSON.stringify(STRUCTURED_DATA);
    document.head.appendChild(script);

    return () => {
      document.title = previousTitle;
      if (metaDescription) metaDescription.setAttribute('content', previousDescription);
      if (metaKeywords) metaKeywords.setAttribute('content', previousKeywords);
      if (canonicalLink) canonicalLink.setAttribute('href', previousCanonical);
      const injected = document.getElementById('case-study-schema');
      if (injected) injected.remove();
    };
  }, []);

  return (
    <div className='min-h-screen bg-brand-cream text-brand-charcoal font-body'>

      {/* Header */}
      <header className='border-b border-brand-coffee/10 bg-brand-warmwhite/95 backdrop-blur-sm sticky top-0 z-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-6'>
          <Link to='/' className='text-left'>
            <Wordmark />
          </Link>
          <nav className='hidden md:flex items-center gap-8'>
            <Link
              to='/case-studies'
              className='inline-flex items-center gap-1.5 font-body text-sm font-medium uppercase text-brand-coffee hover:text-brand-ember transition-colors'
              style={{ letterSpacing: '0.18em' }}
            >
              <ArrowLeft size={14} />
              Case Studies
            </Link>
            <Link
              to='/'
              className='font-body text-sm font-medium uppercase text-brand-coffee hover:text-brand-ember transition-colors'
              style={{ letterSpacing: '0.18em' }}
            >
              Home
            </Link>
            <Link
              to='/#contact'
              className='font-body text-sm font-medium uppercase text-brand-coffee hover:text-brand-ember transition-colors'
              style={{ letterSpacing: '0.18em' }}
            >
              Contact
            </Link>
          </nav>
          <a
            href='tel:+18882576123'
            className='hidden sm:flex items-center gap-2 font-body text-sm font-medium text-brand-coffee hover:text-brand-ember transition-colors'
          >
            <Phone size={16} className='text-brand-ember' />
            (888) 257-6123
          </a>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className='bg-brand-warmwhite border-b border-brand-coffee/10'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3'>
          <nav aria-label='Breadcrumb'>
            <ol className='flex items-center gap-2 font-body text-xs text-brand-stone' style={{ letterSpacing: '0.1em' }}>
              <li><Link to='/' className='hover:text-brand-ember transition-colors'>Home</Link></li>
              <li><ChevronRight size={12} /></li>
              <li><Link to='/case-studies' className='hover:text-brand-ember transition-colors'>Case Studies</Link></li>
              <li><ChevronRight size={12} /></li>
              <li className='text-brand-coffee font-semibold truncate max-w-xs'>Reclaiming Revenue & Restoring Trust</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className='bg-brand-navy py-20 sm:py-28 relative overflow-hidden'>
        <div
          className='absolute inset-0 opacity-5'
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 60px, #D2691E 60px, #D2691E 61px)',
          }}
          aria-hidden='true'
        />
        <div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='max-w-3xl'>
            <Eyebrow className='text-brand-ember mb-5'>Case Study · Independent Pathology Laboratory</Eyebrow>
            <h1 className='font-display font-bold text-brand-warmwhite text-4xl sm:text-5xl md:text-6xl leading-[1.05] mb-6'>
              Reclaiming Revenue{' '}
              <em className='text-brand-ember'>and Restoring Trust</em>
            </h1>
            <p className='font-body text-xl text-brand-cream/80 leading-relaxed mb-10 max-w-2xl'>
              How Horizon RCM transformed billing operations for an independent pathology
              practice — resolving chronic modifier errors, building real transparency,
              and delivering sustainable revenue growth.
            </p>
            <div className='flex flex-col sm:flex-row sm:items-center gap-5'>
              <div className='inline-flex items-center'>
                <CtaChevrons side='left' />
                <Link
                  to='/consultation'
                  className='inline-block bg-brand-ember hover:bg-brand-coffee text-brand-warmwhite font-body font-semibold text-sm uppercase py-4 px-10 transition-colors duration-300'
                  style={{ letterSpacing: '0.18em' }}
                >
                  Get a Free Consultation
                </Link>
                <CtaChevrons side='right' />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className='bg-brand-ember' aria-label='Key results'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-brand-coffee/20'>
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className='py-8 px-6 sm:px-10 flex items-start gap-5'>
                  <Icon size={32} className='text-brand-warmwhite/70 flex-shrink-0 mt-1' />
                  <div>
                    <div className='font-display font-bold text-brand-warmwhite text-3xl sm:text-4xl leading-none mb-1'>
                      {stat.value}
                    </div>
                    <div
                      className='font-body text-[10px] font-semibold uppercase text-brand-warmwhite/80 mb-1'
                      style={{ letterSpacing: '0.2em' }}
                    >
                      {stat.label}
                    </div>
                    <div className='font-body text-xs text-brand-warmwhite/60 leading-snug'>
                      {stat.detail}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24'>
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-16 items-start'>

          {/* Article Body */}
          <article className='lg:col-span-8' itemScope itemType='https://schema.org/Article'>
            <meta itemProp='headline' content='How Horizon RCM Transformed Billing Operations for an Independent Pathology Practice' />
            <meta itemProp='datePublished' content='2025-01-01' />
            <meta itemProp='author' content='Horizon RCM' />

            {/* Background */}
            <section className='mb-14'>
              <Eyebrow className='text-brand-ember mb-3'>Background</Eyebrow>
              <h2 className='font-display font-bold text-brand-coffee text-3xl sm:text-4xl mb-6 leading-snug'>
                A Practice Carrying the Weight of Compounding Billing Errors
              </h2>
              <div className='space-y-5 font-body text-brand-charcoal/85 text-lg leading-relaxed'>
                <p>
                  When Horizon RCM first engaged with this independent pathology laboratory,
                  the practice was in a state of chronic revenue underperformance. The lab had
                  cycled through multiple third-party billing companies over the years,
                  accumulating a pattern of compounding errors that had quietly eroded
                  collections and strained the physician's confidence in the billing process entirely.
                </p>
                <p>
                  The most consequential and persistent error involved{' '}
                  <strong className='text-brand-coffee'>modifier usage</strong>. Because this
                  pathology practice performed only the professional component of its services,
                  every claim should have carried <strong className='text-brand-coffee'>Modifier 26</strong>,
                  which designates the professional interpretation separately from the technical
                  component. Instead, prior billers had routinely submitted claims using the
                  global billing code — bundling both the professional and technical components
                  into a single reimbursement rate.
                </p>
                <p>
                  For a laboratory that was not performing nor billing for the technical work,
                  this meant the practice was regularly being reimbursed at a global rate it was
                  not entitled to, and that payers were increasingly flagging for overpayment
                  review. The financial and compliance exposure this created was significant.
                </p>
                <p>
                  Beyond the modifier issue, the practice had little visibility into its own
                  claims data. Denials were worked inconsistently, follow-up timelines were
                  opaque, and the provider had no practical way to audit individual claims
                  without submitting requests to the billing company and waiting days for a
                  response. Trust had eroded to a point where the physician was uncertain
                  whether any biller could be held to a meaningful standard of accuracy and
                  transparency.
                </p>
              </div>
            </section>

            <div className='h-px bg-brand-coffee/10 mb-14' />

            {/* The Approach */}
            <section className='mb-14'>
              <Eyebrow className='text-brand-ember mb-3'>The Horizon RCM Approach</Eyebrow>
              <h2 className='font-display font-bold text-brand-coffee text-3xl sm:text-4xl mb-6 leading-snug'>
                Precision Coding and Systematic Denial Management from Day One
              </h2>
              <div className='space-y-5 font-body text-brand-charcoal/85 text-lg leading-relaxed'>
                <p>
                  Horizon RCM conducted a full intake review before submitting a single claim.
                  Our credentialing team confirmed the practice's enrollment status and payer
                  contracts, and our billing analysts audited a sample of prior submissions to
                  document the scope of the modifier problem. The findings confirmed that global
                  billing had been the default across the practice's claim history with multiple
                  payers, with no systematic correction or internal review process in place.
                </p>
                <p>
                  From the initial submission date forward, every claim was coded with
                  Modifier 26 applied correctly, with our internal quality review process
                  flagging any code pairing that did not match the lab's documented service
                  scope. We established a clear separation between what this practice
                  provided — professional pathology interpretation — and what it did not,
                  the technical component, and built that distinction into our workflow at
                  the claim level.
                </p>
                <p>
                  We also introduced structured denial management from the outset. Rather
                  than treating denials as isolated events, we categorized each denial by
                  reason code, tracked recurrence patterns, and used that data to refine
                  submission protocols over time. Payer-specific quirks that had previously
                  caused systematic write-offs were identified and corrected through updated
                  claim templates and submission timing adjustments.
                </p>
              </div>

              {/* Pull quote */}
              <blockquote className='my-10 border-l-4 border-brand-ember pl-8 py-2'>
                <p className='font-display italic text-brand-coffee text-xl sm:text-2xl leading-relaxed'>
                  "Modifier accuracy is not a preference — it is the foundation. Transparency
                  is not a feature; it is an expectation. When those two things are in place,
                  collections follow."
                </p>
                <cite className='mt-4 block font-body text-sm text-brand-stone not-italic' style={{ letterSpacing: '0.1em' }}>
                  — Horizon RCM
                </cite>
              </blockquote>
            </section>

            <div className='h-px bg-brand-coffee/10 mb-14' />

            {/* Transparency */}
            <section className='mb-14'>
              <Eyebrow className='text-brand-ember mb-3'>Transparency & Provider Access</Eyebrow>
              <h2 className='font-display font-bold text-brand-coffee text-3xl sm:text-4xl mb-6 leading-snug'>
                Claim-Level Visibility the Provider Could Verify Independently
              </h2>
              <div className='space-y-5 font-body text-brand-charcoal/85 text-lg leading-relaxed'>
                <p>
                  One of the physician's most direct requests was the ability to look up any
                  claim at any time. Previous billing relationships had created a black-box
                  experience where the provider had to take collection figures on faith — with
                  no ability to independently verify outcomes.
                </p>
                <p>
                  Horizon RCM addressed this by building claim-level accessibility into the
                  practice's reporting structure. The provider was given the ability to pull up
                  any individual claim, review its submission history, payment status, and any
                  associated correspondence, and conduct unannounced spot checks against the
                  accounts receivable ledger. This was not a reporting dashboard with filtered
                  summaries; it was{' '}
                  <strong className='text-brand-coffee'>
                    direct, claim-level transparency available on demand
                  </strong>
                  .
                </p>
                <p>
                  In practice, the physician began conducting regular spot checks within the
                  first two months of the engagement. Those checks consistently confirmed
                  accurate modifier usage, timely follow-up on outstanding claims, and
                  appropriate write-off documentation where contractual adjustments applied.
                  Over time, the spot-check process became a routine part of the practice's
                  internal governance rather than a reactive audit mechanism — which was
                  exactly the outcome we intended.
                </p>
              </div>
            </section>

            <div className='h-px bg-brand-coffee/10 mb-14' />

            {/* Expanding Services */}
            <section className='mb-14'>
              <Eyebrow className='text-brand-ember mb-3'>Service Expansion</Eyebrow>
              <h2 className='font-display font-bold text-brand-coffee text-3xl sm:text-4xl mb-6 leading-snug'>
                Expanding Organically Around What the Practice Actually Needed
              </h2>
              <div className='space-y-5 font-body text-brand-charcoal/85 text-lg leading-relaxed'>
                <p>
                  As the billing relationship matured, Horizon RCM's engagement with the
                  practice expanded organically based on what the physician actually needed —
                  not a predetermined service package. Early conversations revealed that the
                  practice was managing credentialing renewals manually, tracking contract
                  expiration dates on a spreadsheet, and responding to re-credentialing
                  requests reactively. We incorporated credentialing support into the
                  engagement, providing proactive tracking of enrollment deadlines and
                  managing correspondence with payers directly.
                </p>
                <p>
                  The practice also expressed interest in better understanding its payer mix
                  and how reimbursement rates compared across contracts. Horizon RCM
                  developed a periodic payer performance summary that allowed the physician
                  to see, at a glance, which contracts were performing well against allowed
                  amounts and where renegotiation or additional enrollment might improve net
                  collections.
                </p>
                <p>
                  This iterative approach — beginning with core billing and expanding only
                  where the provider identified genuine need — allowed the practice to avoid
                  paying for services it did not use while ensuring that Horizon RCM's
                  involvement scaled appropriately as trust and scope both grew.
                </p>
              </div>
            </section>

            <div className='h-px bg-brand-coffee/10 mb-14' />

            {/* Outcomes */}
            <section className='mb-14'>
              <Eyebrow className='text-brand-ember mb-3'>Outcomes</Eyebrow>
              <h2 className='font-display font-bold text-brand-coffee text-3xl sm:text-4xl mb-6 leading-snug'>
                Measurable Results Across Revenue, Compliance, and Trust
              </h2>
              <p className='font-body text-brand-charcoal/85 text-lg leading-relaxed mb-8'>
                Within the first full quarter under Horizon RCM's management, the practice's
                results were clear across every dimension — financial, operational, and
                relational.
              </p>
              <ul className='space-y-4' aria-label='Case study outcomes'>
                {outcomes.map((outcome, i) => (
                  <li key={i} className='flex items-start gap-4'>
                    <CheckCircle
                      className='text-brand-ember flex-shrink-0 mt-1'
                      size={20}
                    />
                    <span className='font-body text-brand-charcoal/85 text-lg leading-relaxed'>
                      {outcome}
                    </span>
                  </li>
                ))}
              </ul>

              <div className='mt-12 bg-brand-navy p-8 sm:p-10'>
                <Eyebrow className='text-brand-ember mb-4'>The Takeaway</Eyebrow>
                <p className='font-display font-bold text-brand-warmwhite text-xl sm:text-2xl leading-relaxed'>
                  Pathology billing done correctly requires both technical precision and
                  genuine accountability. The physician now knows what is being billed, why,
                  and how it is performing — and that knowledge has become a routine part of
                  how the practice operates.
                </p>
              </div>
            </section>
          </article>

          {/* Sidebar */}
          <aside className='lg:col-span-4 lg:sticky lg:top-28 space-y-8'>

            {/* Client Snapshot */}
            <div className='bg-brand-warmwhite border border-brand-coffee/10 p-8 shadow-sm'>
              <Eyebrow className='text-brand-stone/80 mb-5'>Client Snapshot</Eyebrow>
              <dl className='space-y-5'>
                {[
                  { icon: FileText, label: 'Practice Type', value: 'Independent Pathology Laboratory' },
                  { icon: MapPin, label: 'Geography', value: 'Texas' },
                  { icon: Shield, label: 'Prior Billing', value: 'Multiple third-party vendors' },
                  { icon: Award, label: 'Services Billed', value: 'Professional component (Modifier 26) only' },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className='flex items-start gap-3'>
                    <Icon size={16} className='text-brand-ember mt-1 flex-shrink-0' />
                    <div>
                      <dt
                        className='font-body text-[10px] font-semibold uppercase text-brand-stone mb-0.5'
                        style={{ letterSpacing: '0.2em' }}
                      >
                        {label}
                      </dt>
                      <dd className='font-body text-brand-coffee text-sm leading-snug'>
                        {value}
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>

            {/* CTA Form */}
            <div className='bg-brand-warmwhite border border-brand-coffee/10 p-8 shadow-sm'>
              <ConsultationForm
                idPrefix='case-study'
                source='New consultation request from Dr. Jim case study page.'
                title='See Similar Results'
                description="Enter your email and we'll reach out to discuss your lab's billing challenges."
              />
            </div>

            {/* Trust signals */}
            <div className='space-y-4 px-1'>
              <div className='flex items-start gap-3'>
                <Shield size={16} className='text-brand-ember flex-shrink-0 mt-0.5' />
                <p className='font-body text-sm text-brand-stone leading-snug'>
                  HIPAA-compliant · Your information is never shared or sold
                </p>
              </div>
              <div className='flex items-center gap-3'>
                <Mail size={16} className='text-brand-ember flex-shrink-0' />
                <a
                  href='mailto:main@horizon-rcm.com'
                  className='font-body text-sm text-brand-coffee hover:text-brand-ember transition-colors'
                >
                  main@horizon-rcm.com
                </a>
              </div>
              <div className='flex items-center gap-3'>
                <Phone size={16} className='text-brand-ember flex-shrink-0' />
                <a
                  href='tel:+18882576123'
                  className='font-body text-sm text-brand-coffee hover:text-brand-ember transition-colors'
                >
                  (888) 257-6123
                </a>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Bottom CTA Banner */}
      <section className='bg-brand-warmwhite border-t border-brand-coffee/10 py-16'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <Eyebrow className='text-brand-ember mb-4'>Ready for a Change?</Eyebrow>
          <h2 className='font-display font-bold text-brand-coffee text-3xl sm:text-4xl mb-5'>
            Your pathology lab deserves billing that{' '}
            <em className='text-brand-ember'>actually works.</em>
          </h2>
          <p className='font-body text-lg text-brand-stone leading-relaxed mb-8 max-w-2xl mx-auto'>
            Whether you're dealing with modifier errors, denial backlogs, or zero visibility
            into your claims — Horizon RCM can help. No pressure, no obligation.
          </p>
          <div className='flex flex-col sm:flex-row items-center justify-center gap-5'>
            <div className='inline-flex items-center'>
              <CtaChevrons side='left' />
              <Link
                to='/consultation'
                className='inline-block bg-brand-ember hover:bg-brand-coffee text-brand-warmwhite font-body font-semibold text-sm uppercase py-4 px-10 transition-colors duration-300'
                style={{ letterSpacing: '0.18em' }}
              >
                Request a Free Consultation
              </Link>
              <CtaChevrons side='right' />
            </div>
            <Link
              to='/case-studies'
              className='font-body text-sm font-semibold uppercase text-brand-coffee hover:text-brand-ember border-b border-brand-coffee hover:border-brand-ember pb-0.5 transition-colors duration-300'
              style={{ letterSpacing: '0.18em' }}
            >
              View All Case Studies
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='bg-brand-navy py-12 border-t-4 border-brand-ember'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-6'>
          <Link to='/'>
            <Wordmark onDark />
          </Link>
          <div className='text-center sm:text-right'>
            <Link
              to='/case-studies'
              className='font-body text-sm text-brand-cream/70 hover:text-brand-ember transition-colors'
            >
              ← All Case Studies
            </Link>
            <p
              className='font-body text-brand-cream/40 mt-2 text-[10px] font-semibold uppercase'
              style={{ letterSpacing: '0.28em' }}
            >
              Exclusive Pathology Billing · Pathology Revenue Cycle Management · 100% US-Based
            </p>
          </div>
        </div>
        <p className='font-body text-brand-cream/40 text-xs text-center mt-8'>
          © 2025 Horizon RCM Partners LLC. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default CaseStudyDrJim;
